import { useState, useEffect, useRef } from 'react';
import '../Styles/ourpartner.css';

const whyCards = [
  {
    num: '01',
    title: 'Reach the Right HR Audience',
    text: 'Connect with HR leaders, CHROs, CXOs, talent professionals, business leaders, placement officers, and the wider HR community.',
  },
  {
    num: '02',
    title: 'Build Brand Visibility',
    text: 'Position your organization in front of a growing professional community through events, digital platforms, content, campaigns, and HR initiatives.',
  },
  {
    num: '03',
    title: 'Create Business Opportunities',
    text: 'Build meaningful relationships with organizations and decision-makers who are shaping the future of people, technology, and work.',
  },
  {
    num: '04',
    title: 'Share Knowledge & Expertise',
    text: 'Showcase your expertise through leadership sessions, panel discussions, webinars, articles, workshops, masterclasses, and community engagements.',
  },
  {
    num: '05',
    title: 'Strengthen Employer & Brand Reputation',
    text: 'Associate your brand with impactful HR conversations, professional development, workplace initiatives, and industry leadership.',
  },
];

// Edit these option lists as you need
const partnershipTypes = [
  'Event Partner',
  'Knowledge Partner',
  'Community Partner',
  'Education Partner',
  'Technology Partner',
  'Strategic Partner',
  'Other',
];

const collaborateOptions = [
  'Branded Content, Expert Articles & Thought Leaderships',
  'Leadership Podcasts & CHRO / CXO Interviews',
  'Sponsored Events & Roundtable Conferences',
];

const initialFormState = {
  fullName: '',
  designation: '',
  companyName: '',
  officialEmail: '',
  contactNumber: '',
  website: '',
  partnershipType: '',
  collaborateOn: '',
};

// ---------- Validation ----------
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^\+?[0-9\s\-()]{7,20}$/;
const URL_PATTERN = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[^\s]*)?$/i;

function validateForm(data) {
  const errors = {};

  if (!data.fullName.trim()) {
    errors.fullName = 'Full name is required.';
  } else if (data.fullName.trim().length < 2) {
    errors.fullName = 'Full name looks too short.';
  }

  if (!data.designation.trim()) {
    errors.designation = 'Designation is required.';
  }

  if (!data.companyName.trim()) {
    errors.companyName = 'Company name is required.';
  } else if (data.companyName.trim().length < 2) {
    errors.companyName = 'Company name looks too short.';
  }

  if (!data.officialEmail.trim()) {
    errors.officialEmail = 'Official email is required.';
  } else if (!EMAIL_PATTERN.test(data.officialEmail.trim())) {
    errors.officialEmail = 'Enter a valid email address.';
  }

  if (!data.contactNumber.trim()) {
    errors.contactNumber = 'Contact number is required.';
  } else if (!PHONE_PATTERN.test(data.contactNumber.trim())) {
    errors.contactNumber = 'Enter a valid contact number.';
  }

  if (!data.website.trim()) {
    errors.website = 'Website link is required.';
  } else if (!URL_PATTERN.test(data.website.trim())) {
    errors.website = 'Enter a valid website URL.';
  }

  if (!data.partnershipType) {
    errors.partnershipType = 'Please select a partnership type.';
  }

  if (!data.collaborateOn) {
    errors.collaborateOn = 'Please select what you would like to collaborate on.';
  }

  return errors;
}

function OurPartners() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const overlayRef = useRef(null);

  const openModal = () => setIsModalOpen(true);

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData(initialFormState);
    setErrors({});
    setSubmitError('');
    setSubmitSuccess(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear that field's error as soon as the user edits it.
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      const res = await fetch('/api/collaborators', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }

      setSubmitSuccess(true);
      setFormData(initialFormState);
      setTimeout(() => {
        closeModal();
      }, 2500);
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Escape to close + lock page scroll while modal is open
  useEffect(() => {
    if (!isModalOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') closeModal();
    };

    document.addEventListener('keydown', handleEscape);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) closeModal();
  };

  const renderButton = () => (
    <button type="button" className="sp-btn" onClick={openModal}>
      Become a Strategic Partner
      <img
        src="/assets/arrow _icon_get.png"
        alt="Arrow"
        className="sp-arrow-icon"
      />
    </button>
  );

  return (
    <>
      {/* ===== SECTION 1 : HERO ===== */}
      <section className="sp-hero">
        <div className="sp-hero-inner">
          <div className="sp-hero-left">
            <h1 className="sp-hero-heading">
              Build Meaningful Connections.
              <span className="sp-hero-highlight">Grow Together.</span>
            </h1>

            <p className="sp-hero-text">
              Partner with Vanakkam HRD to connect your brand with a powerful community of HR leaders, CHROs, business leaders, CEOs, talent professionals, academicians and future HR professionals
            </p>

            <p className="sp-hero-text">
             Together, let’s create meaningful conversations, impactful experiences and long-term value for the HR ecosystem.
            </p>

            {renderButton()}
          </div>

          <div className="sp-hero-right">
            
            <img
              src="/assets/Ourprog_img.png"
              alt="Strategic partners"
              className="sp-hero-img"
            />
          </div>
        </div>
      </section>

      {/* ===== SECTION 2 : WHY PARTNER ===== */}
      <section className="sp-why">
        <div className="sp-why-inner">
          <h2 className="sp-why-heading">Why Partner With Vanakkam HRD?</h2>
          <div className="sp-why-underline"></div>

          <div className="sp-why-grid">
            {whyCards.map((card) => (
              <div className="sp-why-card" key={card.num}>
                <span className="sp-why-num">{card.num}</span>
                <h3 className="sp-why-title">{card.title}</h3>
                <p className="sp-why-text">{card.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 3 : CTA ===== */}
      <section className="sp-cta-wrap">
        <div className="sp-cta">
          <h2 className="sp-cta-heading">Let’s Build Something Meaningful Together</h2>
          <p className="sp-cta-text">
            Strategic partnerships are not just about sponsorships. They are about shared vision, meaningful connections, knowledge exchange, and long-term impact.
          </p>
          <p className="sp-cta-text">
           If your organization wants to connect with the HR community, strengthen its brand presence, showcase expertise, or create new business and talent opportunities, let’s collaborate.
          </p>
          {renderButton()}
        </div>
      </section>

      {/* ===== STRATEGIC PARTNER FORM MODAL ===== */}
      {isModalOpen && (
        <div
          className="sp-modal-overlay"
          ref={overlayRef}
          onMouseDown={handleOverlayClick}
        >
          <div
            className="sp-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sp-modal-title"
          >
            <button
              type="button"
              className="sp-modal-close"
              onClick={closeModal}
              aria-label="Close form"
            >
              &times;
            </button>

            <h2 id="sp-modal-title" className="sp-modal-title">
              Become a <span className="sp-modal-title-highlight">Strategic Partner</span>
            </h2>

            <form className="sp-form" onSubmit={handleSubmit} noValidate>
              <div className="sp-field">
                <label htmlFor="fullName">
                  Full Name<span className="sp-required">*</span>
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={handleChange}
                  className={errors.fullName ? 'sp-input-error' : ''}
                  aria-invalid={!!errors.fullName}
                />
                {errors.fullName && <span className="sp-error-msg">{errors.fullName}</span>}
              </div>

              <div className="sp-field">
                <label htmlFor="designation">
                  Designation <span className="sp-required">*</span>
                </label>
                <input
                  type="text"
                  id="designation"
                  name="designation"
                  placeholder="Enter Designation"
                  value={formData.designation}
                  onChange={handleChange}
                  className={errors.designation ? 'sp-input-error' : ''}
                  aria-invalid={!!errors.designation}
                />
                {errors.designation && <span className="sp-error-msg">{errors.designation}</span>}
              </div>

              <div className="sp-field">
                <label htmlFor="companyName">
                  Company Name <span className="sp-required">*</span>
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  placeholder="Enter Company Name"
                  value={formData.companyName}
                  onChange={handleChange}
                  className={errors.companyName ? 'sp-input-error' : ''}
                  aria-invalid={!!errors.companyName}
                />
                {errors.companyName && <span className="sp-error-msg">{errors.companyName}</span>}
              </div>

              <div className="sp-field">
                <label htmlFor="officialEmail">
                  Official Email ID <span className="sp-required">*</span>
                </label>
                <input
                  type="email"
                  id="officialEmail"
                  name="officialEmail"
                  placeholder="Enter Official Email ID"
                  value={formData.officialEmail}
                  onChange={handleChange}
                  className={errors.officialEmail ? 'sp-input-error' : ''}
                  aria-invalid={!!errors.officialEmail}
                />
                {errors.officialEmail && <span className="sp-error-msg">{errors.officialEmail}</span>}
              </div>

              <div className="sp-field">
                <label htmlFor="contactNumber">
                  Contact Number<span className="sp-required">*</span>
                </label>
                <input
                  type="tel"
                  id="contactNumber"
                  name="contactNumber"
                  placeholder="Enter Contact Number"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  className={errors.contactNumber ? 'sp-input-error' : ''}
                  aria-invalid={!!errors.contactNumber}
                />
                {errors.contactNumber && <span className="sp-error-msg">{errors.contactNumber}</span>}
              </div>

              <div className="sp-field">
                <label htmlFor="website">
                  Website <span className="sp-required">*</span>
                </label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  placeholder="Enter Website Link"
                  value={formData.website}
                  onChange={handleChange}
                  className={errors.website ? 'sp-input-error' : ''}
                  aria-invalid={!!errors.website}
                />
                {errors.website && <span className="sp-error-msg">{errors.website}</span>}
              </div>

              <div className="sp-field">
                <label htmlFor="partnershipType">
                  Partnership Type <span className="sp-required">*</span>
                </label>
                <select
                  id="partnershipType"
                  name="partnershipType"
                  value={formData.partnershipType}
                  onChange={handleChange}
                  className={`${errors.partnershipType ? 'sp-input-error' : ''} ${
                    formData.partnershipType === '' ? 'sp-select-placeholder' : ''
                  }`}
                  aria-invalid={!!errors.partnershipType}
                >
                  <option value="" disabled>
                    Select Type
                  </option>
                  {partnershipTypes.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {errors.partnershipType && <span className="sp-error-msg">{errors.partnershipType}</span>}
              </div>

              <div className="sp-field">
                <label htmlFor="collaborateOn">
                  What would you like to collaborate on? <span className="sp-required">*</span>
                </label>
                <select
                  id="collaborateOn"
                  name="collaborateOn"
                  value={formData.collaborateOn}
                  onChange={handleChange}
                  className={`${errors.collaborateOn ? 'sp-input-error' : ''} ${
                    formData.collaborateOn === '' ? 'sp-select-placeholder' : ''
                  }`}
                  aria-invalid={!!errors.collaborateOn}
                >
                  <option value="" disabled>
                    Select would you like to collaborate on
                  </option>
                  {collaborateOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {errors.collaborateOn && <span className="sp-error-msg">{errors.collaborateOn}</span>}
              </div>

              {submitError && (
                <span className="sp-error-msg sp-submit-error">{submitError}</span>
              )}

              <button
                type="submit"
                className="sp-submit-btn"
                disabled={submitting || submitSuccess}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>

              {submitSuccess && (
                <p className="sp-success-msg">
                  Thanks for submitting! We'll get back to you soon.
                </p>
              )}
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default OurPartners;